import { useState, useEffect } from "react";
import axios from "axios";
import api from "../../services/api";
import type { Customer } from "../../services/customerService";

interface EditCustomerFormProps {
  customer: Customer;
  onCustomerUpdated: () => void;
  onCancel: () => void;
}

/**
 * Form component for editing an existing customer
 */
const EditCustomerForm = ({ customer, onCustomerUpdated, onCancel }: EditCustomerFormProps) => {
  const [formData, setFormData] = useState({
    customerNumber: customer.customerNumber || "",
    customerName: customer.customerName || "",
    primaryContactEmail: customer.primaryContactEmail || "",
    entraTenantId: customer.entraTenantId || ""
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update form data if customer prop changes
  useEffect(() => {
    setFormData({
      customerNumber: customer.customerNumber || "",
      customerName: customer.customerName || "",
      primaryContactEmail: customer.primaryContactEmail || "",
      entraTenantId: customer.entraTenantId || ""
    });
  }, [customer]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.customerNumber || !formData.customerName) {
      setError("Customer Number and Customer Name are required");
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Prepare data - remove empty strings
      const dataToSubmit = {
        customerName: formData.customerName,
        primaryContactEmail: formData.primaryContactEmail || null,
        entraTenantId: formData.entraTenantId || null
      };
      
      // Make API request
      await api.put(`/customer/${customer.customerId}`, dataToSubmit);
      
      // Notify parent component
      onCustomerUpdated();
    } catch (err) {
      console.error("Error updating customer:", err);
      
      // Provide detailed error message
      if (axios.isAxiosError(err) && err.response) {
        setError(`Failed to update customer: ${err.response.status} ${err.response.statusText}. ${err.response.data?.error || ''}`);
      } else {
        setError("Failed to update customer. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card p-lg">
      <h2 className="text-primary mb-md">Edit Customer</h2>
      
      {error && (
        <div className="bg-error p-sm mb-md" style={{ borderRadius: 'var(--border-radius-sm)' }}>
          <p className="text-white">{error}</p>
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="mb-md">
          <label htmlFor="customerNumber">Customer Number *</label>
          <input
            type="text"
            id="customerNumber"
            name="customerNumber"
            value={formData.customerNumber}
            onChange={handleChange}
            required
            disabled // Customer number cannot be changed
            className="w-full"
            style={{ width: '100%', opacity: 0.7 }}
          />
          <small className="text-secondary">Customer number cannot be changed</small>
        </div>
        
        <div className="mb-md">
          <label htmlFor="customerName">Customer Name *</label>
          <input
            type="text"
            id="customerName"
            name="customerName"
            value={formData.customerName}
            onChange={handleChange}
            required
            style={{ width: '100%' }}
          />
        </div>
        
        <div className="mb-md">
          <label htmlFor="primaryContactEmail">Primary Contact Email</label>
          <input
            type="email"
            id="primaryContactEmail"
            name="primaryContactEmail"
            value={formData.primaryContactEmail}
            onChange={handleChange}
            style={{ width: '100%' }}
          />
        </div>
        
        <div className="mb-lg">
          <label htmlFor="entraTenantId">Entra Tenant ID</label>
          <input
            type="text"
            id="entraTenantId"
            name="entraTenantId"
            value={formData.entraTenantId}
            onChange={handleChange}
            style={{ width: '100%' }}
          />
        </div>
        
        <div className="flex justify-between">
          <button 
            type="button" 
            onClick={onCancel}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="spinner mr-sm" style={{ width: '16px', height: '16px' }}></div>
                Updating...
              </>
            ) : 'Update Customer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditCustomerForm;
